'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Loader2, Send, CheckCircle2, AlertCircle, Folder, X, Search } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { MarkdownEditor } from '@/components/ui/markdown-editor'
import { useToast } from '@/hooks/use-toast'
import { getAuthHeaders } from '@/lib/api-utils'

interface NewsPost {
  projectId: number
  projectName: string
  title: string
  description: string
  issueIds: number[]
}

interface Project {
  id: number
  name: string
  identifier: string
}

interface InlinePostNewsFormProps {
  insights: string
  timeEntries?: any[]
  onClose: () => void
  onSuccess?: () => void
  onCloseAll?: () => void // Close all sidebars including the main assistant
}

export function InlinePostNewsForm({ insights, timeEntries = [], onClose, onSuccess, onCloseAll }: InlinePostNewsFormProps) {
  const [loading, setLoading] = useState(false)
  const [parsing, setParsing] = useState(false)
  const [summarizing, setSummarizing] = useState(false)
  const [newsPosts, setNewsPosts] = useState<NewsPost[]>([])
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [projects, setProjects] = useState<Project[]>([])
  const [loadingProjects, setLoadingProjects] = useState(false)
  const [projectSearchQueries, setProjectSearchQueries] = useState<{[key: number]: string}>({})
  const { toast } = useToast()
  const router = useRouter()

  useEffect(() => {
    fetchProjects()
    parseInsights()
  }, [])

  const fetchProjects = async () => {
    setLoadingProjects(true)
    try {
      const authHeaders = getAuthHeaders()
      const response = await fetch('/api/projects?limit=1000', { headers: authHeaders })
      if (response.ok) {
        const data = await response.json()
        setProjects(data.projects || [])
      }
    } catch (err) {
      console.error('Error fetching projects:', err)
    } finally {
      setLoadingProjects(false)
    }
  }

  const parseInsights = async () => {
    setParsing(true)
    setSummarizing(false) // Don't call AI again
    try {
      const authHeaders = getAuthHeaders()
      
      // Use "Daily Updates - [Today's Date]" as the title always
      const today = new Date()
      const formattedDate = today.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
      const achievementTitle = `Daily Updates - ${formattedDate}`
      let summarizedInsights = insights // Use the summary we already have

      // Clean up the insights text for news posting
      // Remove section headers and just keep the content
      summarizedInsights = insights
        .replace(/TIME SUMMARY:\s*/gi, '')
        .replace(/TASK DETAILS:[\s\S]*?(?=QUALITY ALERTS:|NEXT STEPS:|$)/gi, '')
        .replace(/QUALITY ALERTS:[\s\S]*?(?=NEXT STEPS:|$)/gi, '')
        .replace(/NEXT STEPS:[\s\S]*/gi, '')
        .replace(/Confidence:.*$/gm, '')
        .replace(/\*\*/g, '') // Remove markdown bold markers
        .replace(/^#+\s*/gm, '') // Remove markdown headers
        .trim()

      // Fallback: create a simple summary if cleaning failed
      if (!summarizedInsights) {
        const lines = insights
          .split('\n')
          .filter((l: string) => l.trim() && !l.includes('**') && !l.includes('NEXT STEPS') && !l.includes('QUALITY'))
          .map((l: string) => l.replace(/^[-*]\s*/, '').trim())
          .filter((l: string) => l.length > 10)
        summarizedInsights = lines.slice(0, 3).join(' ').substring(0, 300) // Increased to 3 lines and 300 chars
      }
      
      const issueIds = Array.from(new Set(
        timeEntries
          .filter((entry: any) => entry.issue?.id)
          .map((entry: any) => entry.issue.id)
      ))

      if (issueIds.length === 0) {
        setNewsPosts([{
          projectId: 0,
          projectName: '',
          title: achievementTitle,
          description: summarizedInsights,
          issueIds: []
        }])
        setParsing(false)
        return
      }

      const issueDetailsPromises = issueIds.map(id =>
        fetch(`/api/issues/${id}`, { headers: authHeaders })
          .then(res => res.ok ? res.json() : null)
          .then(data => data?.issue)
      )

      const issues = (await Promise.all(issueDetailsPromises)).filter(Boolean)
      const projectGroups = new Map<number, any[]>()
      
      issues.forEach(issue => {
        const projectId = issue.project.id
        if (!projectGroups.has(projectId)) {
          projectGroups.set(projectId, [])
        }
        projectGroups.get(projectId)!.push(issue)
      })

      const posts: NewsPost[] = Array.from(projectGroups.entries()).map(([projectId, projectIssues]) => ({
        projectId,
        projectName: projectIssues[0].project.name,
        title: achievementTitle,
        description: summarizedInsights,
        issueIds: projectIssues.map(i => i.id)
      }))

      setNewsPosts(posts)
    } catch (err) {
      console.error('Error parsing insights:', err)
      setSummarizing(false)
      
      // Fallback summary
      const lines = insights
        .split('\n')
        .filter(l => l.trim() && !l.includes('**') && !l.includes('NEXT STEPS'))
        .map(l => l.replace(/^[-*]\s*/, '').trim())
        .filter(l => l.length > 10)
      const fallbackSummary = lines.slice(0, 2).join(' ').substring(0, 250)
      
      setNewsPosts([{
        projectId: 0,
        projectName: '',
        title: `Daily Achievement - ${new Date().toLocaleDateString()}`,
        description: fallbackSummary,
        issueIds: []
      }])
    } finally {
      setParsing(false)
      setSummarizing(false)
    }
  }

  const updateField = (index: number, field: keyof NewsPost, value: string | number | number[]) => {
    const updated = [...newsPosts]
    ;(updated[index] as any)[field] = value
    setNewsPosts(updated)
  }

  const updateProject = (index: number, projectId: string) => {
    const updated = [...newsPosts]
    const project = projects.find(p => p.id.toString() === projectId)
    if (project) {
      updated[index].projectId = project.id
      updated[index].projectName = project.name
      setNewsPosts(updated)
    }
  }

  const removePost = (index: number) => {
    setNewsPosts(newsPosts.filter((_, i) => i !== index))
  }

  const postAllNews = async () => {
    setLoading(true)
    setError(null)

    try {
      const authHeaders = getAuthHeaders()
      const postPromises = newsPosts.map(async (post) => {
        if (!post.projectId || post.projectId === 0) {
          throw new Error(`Please select a project for: ${post.title}`)
        }

        const newsData = {
          news: {
            title: post.title,
            description: post.description,
            summary: post.description.substring(0, 200)
          }
        }

        const response = await fetch(`/api/projects/${post.projectId}/news`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders
          },
          body: JSON.stringify(newsData)
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.error || `Failed to post to ${post.projectName}`)
        }

        return response.json()
      })

      const results = await Promise.allSettled(postPromises)
      const failures = results.filter(r => r.status === 'rejected')
      const successes = results.filter(r => r.status === 'fulfilled')

      if (failures.length > 0) {
        const failureMessages = failures.map((f: any) => f.reason?.message || 'Unknown error').join('\n')
        setError(`Posted ${successes.length} of ${results.length} news items.\n\nFailures:\n${failureMessages}`)
      } else {
        setSuccess(true)
        window.dispatchEvent(new CustomEvent('newsPosted'))
        
        // Get the first successfully posted news ID for redirection
        const firstSuccess = successes[0] as PromiseFulfilledResult<any>
        const newsId = firstSuccess?.value?.news?.id
        
        
        
        // Show success toast (will appear above all sidebars)
        toast({
          title: 'News Posted Successfully',
          description: `Posted ${successes.length} news ${successes.length === 1 ? 'item' : 'items'} successfully.`,
        })
        
        
        
        // Wait a moment for toast to render, then close sidebars
        setTimeout(() => {
          // Close all sidebars (both post news form and main assistant)
          if (onCloseAll) {
            // Use onCloseAll which will close both sidebars
            onCloseAll()
          } else {
            // Fallback: just close the post news form
            onSuccess?.()
            onClose()
          }
          
          
          
          // Redirect to news page after sidebars close
          setTimeout(() => {
            
            if (newsId) {
              router.push(`/news/${newsId}`)
            } else {
              router.push('/news')
            }
          }, 400)
        }, 200)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to post news')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-4 animate-in slide-in-from-top duration-300">
      <div className="flex items-center">
        <h3 className="text-lg font-semibold">Post as News</h3>
      </div>

      {parsing && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary mr-2" />
          <span className="text-sm text-muted-foreground">
            {summarizing ? 'AI is generating your achievement summary...' : 'Organizing by project...'}
          </span>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive">
          <AlertCircle className="w-4 h-4" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 text-emerald-600">
          <CheckCircle2 className="w-4 h-4" />
          <p className="text-sm font-medium">Successfully posted all news items!</p>
        </div>
      )}

      {!parsing && newsPosts.map((post, index) => (
        <Card key={index} className="border">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center gap-3">
              <Folder className="w-4 h-4 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                {loadingProjects ? (
                  <div className="flex items-center gap-2 px-3 py-2 border border-input rounded-md">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Loading projects...</span>
                  </div>
                ) : (
                  <Select
                    value={post.projectId ? post.projectId.toString() : ''}
                    onValueChange={(value) => updateProject(index, value)}
                    disabled={loading}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select a project..." />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="flex items-center border-b px-3 pb-2 mb-2">
                        <Search className="w-4 h-4 text-muted-foreground mr-2" />
                        <Input
                          placeholder="Search projects..."
                          value={projectSearchQueries[index] || ''}
                          onChange={(e) => {
                            setProjectSearchQueries(prev => ({
                              ...prev,
                              [index]: e.target.value
                            }))
                          }}
                          className="h-8 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 p-0"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        />
                      </div>
                      {projects
                        .filter(project => 
                          !projectSearchQueries[index] || 
                          project.name.toLowerCase().includes(projectSearchQueries[index].toLowerCase())
                        )
                        .map((project) => (
                          <SelectItem key={project.id} value={project.id.toString()}>
                            {project.name}
                          </SelectItem>
                        ))}
                      {projects.filter(project => 
                        !projectSearchQueries[index] || 
                        project.name.toLowerCase().includes(projectSearchQueries[index].toLowerCase())
                      ).length === 0 && (
                        <div className="py-6 text-center text-sm text-muted-foreground">
                          No projects found
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                )}
              </div>
              {post.issueIds.length > 0 && (
                <Badge variant="secondary" className="text-xs flex-shrink-0">
                  {post.issueIds.length} issue{post.issueIds.length !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>

            <Separator />

            <div className="space-y-3">
              <div>
                <Label htmlFor={`title-${index}`}>Title</Label>
                <Input
                  id={`title-${index}`}
                  value={post.title}
                  onChange={(e) => updateField(index, 'title', e.target.value)}
                  disabled={loading}
                />
              </div>

              <div>
                <Label htmlFor={`description-${index}`}>Description</Label>
                <MarkdownEditor
                  value={post.description}
                  onChange={(value) => updateField(index, 'description', value)}
                  placeholder="Enter news description..."
                />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      {!parsing && newsPosts.length > 0 && (
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={postAllNews}
            disabled={loading || newsPosts.some(p => !p.projectId || p.projectId === 0)}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Posting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Post {newsPosts.length} News {newsPosts.length !== 1 ? 'Items' : 'Item'}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  )
}
